import useScopedDocument from '../hooks/useScopedDocument';
// src/pages/AffiliateDisclosure.js
import React, { useEffect } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './AffiliateDisclosure.css';

function AffiliateDisclosure() {
  const { countryCode } = useOutletContext() || {};
  useScopedDocument(countryCode === 'canada');
  const location = useLocation();
  
  // SEO constants
  const siteBaseUrl = 'https://www.originselect.com';
  const pageTitle = 'Affiliate Disclosure - OriginSelect';
  const metaDescription = 'Learn about OriginSelect\'s affiliate relationships and how we maintain transparency while connecting you with authentic Canadian products.';
  const canonicalUrl = `${siteBaseUrl}${location.pathname.replace(/\/$/, '')}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${siteBaseUrl}/country/${countryCode || 'canada'}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Affiliate Disclosure",
        "item": canonicalUrl
      }
    ]
  };
  
  // Set page title and scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet defer={false}>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OriginSelect" />
        <meta property="og:image" content="https://originselect-global-hero-images.s3.amazonaws.com/hero/canada-home-hero-e1f1b08bf8df52f8c45043855f2b06ce.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://originselect-global-hero-images.s3.amazonaws.com/hero/canada-home-hero-e1f1b08bf8df52f8c45043855f2b06ce.png" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        
        {/* Structured Data - AboutPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": metaDescription,
            "url": canonicalUrl,
            "mainEntity": {
              "@type": "Organization",
              "name": "OriginSelect",
              "description": "Transparent affiliate relationships supporting Canadian artisans and small businesses"
            }
          })}
        </script>
      </Helmet>
      
      <div className="affiliate-disclosure-page">
        <div className="page-container">
          <header className="page-header">
            <h1>Affiliate Disclosure</h1>
            <p className="subtitle">Transparency in our partnerships and recommendations</p>
          </header>

          <main className="disclosure-content">
            <section className="disclosure-section">
              <h2>Our Commitment to Transparency</h2>
              <p>
                At OriginSelect, we believe in complete transparency with our community. This disclosure explains how affiliate relationships may influence our content and how we maintain our commitment to authentic Canadian products.
              </p>
            </section>

            <section className="disclosure-section">
              <h2>What Are Affiliate Links?</h2>
              <p>
                Some links on our website are "affiliate links." When you click these links and make a purchase, OriginSelect may receive a small commission at no additional cost to you. These commissions help support our platform and allow us to continue connecting you with authentic Canadian products.
              </p>
              <div className="important-note">
                <strong>Important:</strong> All affiliate links are clearly marked with <code>rel="sponsored"</code> attributes for transparency and search engine compliance.
              </div>
            </section>

            <section className="disclosure-section">
              <h2>Our Editorial Standards</h2>
              <div className="standards-grid">
                <div className="standard-item">
                  <h3>🎯 Authentic Recommendations</h3>
                  <p>We only recommend products we genuinely believe in and that meet our strict Canadian authenticity standards.</p>
                </div>
                <div className="standard-item">
                  <h3>🔍 Independent Review</h3>
                  <p>Our product evaluations are based on quality, authenticity, and value—not commission potential.</p>
                </div>
                <div className="standard-item">
                  <h3>🇨🇦 Canadian-First</h3>
                  <p>We prioritize Canadian-made products and artisans, regardless of affiliate opportunities.</p>
                </div>
                <div className="standard-item">
                  <h3>📝 Clear Disclosure</h3>
                  <p>When content includes affiliate links, we clearly disclose this relationship to our readers.</p>
                </div>
              </div>
            </section>

            <section className="disclosure-section">
              <h2>Types of Affiliate Relationships</h2>
              <div className="affiliate-types">
                <div className="affiliate-type">
                  <h3>Amazon Associates Program</h3>
                  <p>
                    OriginSelect is a participant in the Amazon Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.ca and Amazon.com.
                  </p>
                </div>
                <div className="affiliate-type">
                  <h3>Canadian Retailer Partnerships</h3>
                  <p>
                    We partner with select Canadian retailers and artisan marketplaces that align with our values of authenticity and quality craftsmanship.
                  </p>
                </div>
                <div className="affiliate-type">
                  <h3>Direct Artisan Partnerships</h3>
                  <p>
                    We work directly with Canadian artisans and small businesses to showcase their products while maintaining editorial independence.
                  </p>
                </div>
              </div>
            </section>

            <section className="disclosure-section">
              <h2>How We Use Affiliate Income</h2>
              <ul className="income-usage">
                <li><strong>Platform Development:</strong> Improving website functionality and user experience</li>
                <li><strong>Content Creation:</strong> Researching and creating valuable content about Canadian products</li>
                <li><strong>Canadian Verification:</strong> Funding our rigorous product authenticity verification process</li>
                <li><strong>Community Building:</strong> Supporting Canadian artisan communities and small businesses</li>
              </ul>
            </section>

            <section className="disclosure-section">
              <h2>Your Trust Matters</h2>
              <p>
                We understand that trust is earned, not given. Our affiliate relationships never compromise our commitment to:
              </p>
              <ul>
                <li>Providing honest, unbiased product information</li>
                <li>Maintaining rigorous Canadian authenticity standards</li>
                <li>Supporting genuine Canadian artisans and small businesses</li>
                <li>Delivering value to our community above profit</li>
              </ul>
            </section>

            <section className="disclosure-section">
              <h2>Questions?</h2>
              <p>
                If you have any questions about our affiliate relationships or this disclosure, please don't hesitate to <a href={`/country/${countryCode}/contact`}>contact us</a>. We're committed to maintaining transparency and earning your trust.
              </p>
            </section>

            <div className="last-updated">
              <small><strong>Last Updated:</strong> August 27, 2025</small>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

export default AffiliateDisclosure;
